/**
 * LapBlocks.jsx — Per-50m Split Visualization
 * ==============================================
 *
 * Renders race splits as individual lap blocks.
 * Adapts layout based on race distance:
 *   - 50m/100m/200m: expanded cards, all visible
 *   - 400m+: compact grid, click to expand
 *   - 800m/1500m: grouped by 100m with compact sub-blocks
 *
 * Data source: normalized session.lapBlocks (from normalizeSession.js)
 *
 * @author Arena Stats / Paulo Brant
 * @date   2026-04-09
 */

import React, { useState, useMemo } from 'react';
import {
  Timer, Zap, Activity, Ruler, TrendingUp,
  Anchor, Navigation, Target, ChevronDown, ChevronUp,
  Layers,
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════
// Design Tokens (same as RaceResultPage)
// ═══════════════════════════════════════════════════════════════════

const T = {
  primary: '#111111',
  bg: '#fafafa',
  card: '#ffffff',
  border: '#e5e7eb',
  text: '#111111',
  textLight: '#6b7280',
  textMuted: '#9ca3af',
  success: '#16a34a',
  successBg: '#f0fdf4',
  warning: '#f59e0b',
  warningBg: '#fffbeb',
  error: '#dc2626',
  info: '#3b82f6',
  infoBg: '#eff6ff',
  purple: '#8b5cf6',
  cyan: '#06b6d4',
};

const fmtTime = (s) => {
  if (s == null || isNaN(s) || s === 0) return '—';
  const min = Math.floor(s / 60);
  const sec = s % 60;
  return min > 0 ? `${min}:${sec.toFixed(2).padStart(5, '0')}` : `${sec.toFixed(2)}s`;
};

const phaseIcon = (phase) => {
  if (phase === 'start') return { Icon: Anchor, label: 'Saída', color: T.success };
  if (phase === 'finish') return { Icon: Target, label: 'Chegada', color: T.error };
  return { Icon: Navigation, label: 'Virada', color: T.warning };
};


// ═══════════════════════════════════════════════════════════════════
// Expanded Lap Card (for 50m / 100m / 200m)
// ═══════════════════════════════════════════════════════════════════

const ExpandedLapCard = ({ block, totalStrokes, isBest, isSlowest }) => {
  const { Icon, label: phaseLabel, color: phaseColor } = phaseIcon(block.phase);
  const dps = block.strokes > 0 ? (50 / block.strokes).toFixed(2) : '—';
  const avgInterval = block.avgIntervalS > 0 ? block.avgIntervalS.toFixed(2)
    : (block.strokes > 1 && block.timeS > 0 ? (block.timeS / block.strokes).toFixed(2) : '—');
  const pctOfTotal = totalStrokes > 0 ? Math.round((block.strokes / totalStrokes) * 100) : 0;

  // v3.10: Quality indicator from raceStructure
  const qualityColor = block.quality === 'ok' ? T.success
    : block.quality === 'suspect' ? T.warning
    : block.quality === 'empty' || block.quality === 'overcrowded' ? T.error
    : null;

  return (
    <div className={`rounded-xl border overflow-hidden transition-all hover:shadow-md ${isBest ? 'ring-2 ring-offset-1' : ''}`}
      style={{
        borderColor: isBest ? T.success : isSlowest ? T.warning + '60' : T.border,
        ringColor: isBest ? T.success : undefined,
        backgroundColor: T.card,
      }}>
      {/* Header bar */}
      <div className="px-3 py-2 flex items-center justify-between"
        style={{ backgroundColor: isBest ? T.successBg : '#f9fafb', borderBottom: `1px solid ${T.border}` }}>
        <div className="flex items-center gap-2">
          <Icon size={12} style={{ color: phaseColor }} />
          <span className="text-xs font-black" style={{ color: T.text }}>{block.label}</span>
        </div>
        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
          style={{ backgroundColor: phaseColor + '15', color: phaseColor }}>
          {phaseLabel}
        </span>
        {qualityColor && block.quality !== 'unknown' && (
          <span className="w-2 h-2 rounded-full ml-1" style={{ backgroundColor: qualityColor }}
            title={block.quality === 'ok' ? 'Qualidade OK' : block.warnings?.join(', ') || block.quality} />
        )}
      </div>

      {/* Metrics grid */}
      <div className="p-3 grid grid-cols-2 gap-2">
        {/* Split time */}
        <div className="flex flex-col">
          <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>
            <Timer size={9} className="inline mr-0.5" />Tempo
          </span>
          <span className="text-lg font-black" style={{ color: T.primary }}>{fmtTime(block.timeS)}</span>
        </div>

        {/* Cumulative */}
        <div className="flex flex-col">
          <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>Acumulado</span>
          <span className="text-sm font-bold" style={{ color: T.textLight }}>{fmtTime(block.cumulativeS)}</span>
        </div>

        {/* Strokes */}
        <div className="flex flex-col">
          <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>
            <Zap size={9} className="inline mr-0.5" />Braçadas
          </span>
          <span className="text-lg font-black" style={{ color: T.info }}>
            {block.strokes}
            <span className="text-[9px] font-semibold ml-1" style={{ color: T.textMuted }}>({pctOfTotal}%)</span>
          </span>
        </div>

        {/* Stroke Rate */}
        <div className="flex flex-col">
          <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>
            <Activity size={9} className="inline mr-0.5" />SR
          </span>
          <span className="text-sm font-bold" style={{ color: T.purple }}>
            {block.strokeRate > 0 ? block.strokeRate.toFixed(1) : '—'}
            <span className="text-[9px] ml-0.5" style={{ color: T.textMuted }}>br/min</span>
          </span>
        </div>

        {/* DPS */}
        <div className="flex flex-col">
          <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>
            <Ruler size={9} className="inline mr-0.5" />DPS
          </span>
          <span className="text-sm font-bold" style={{ color: T.cyan }}>
            {dps}<span className="text-[9px] ml-0.5" style={{ color: T.textMuted }}>m/br</span>
          </span>
        </div>

        {/* Avg interval */}
        <div className="flex flex-col">
          <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>Intervalo Médio</span>
          <span className="text-sm font-bold" style={{ color: T.text }}>
            {avgInterval !== '—' ? `${avgInterval}s` : '—'}
          </span>
        </div>
      </div>

      {/* Speed bar (visual indicator) */}
      {block.avgVelocityMs > 0 && (
        <div className="px-3 pb-2">
          <div className="flex items-center gap-1.5">
            <TrendingUp size={9} style={{ color: T.textMuted }} />
            <span className="text-[9px] font-semibold" style={{ color: T.textLight }}>
              {block.avgVelocityMs.toFixed(2)} m/s
            </span>
            <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ backgroundColor: '#f3f4f6' }}>
              <div className="h-full rounded-full" style={{
                width: `${Math.min(100, (block.avgVelocityMs / 2.5) * 100)}%`,
                backgroundColor: isBest ? T.success : T.info,
              }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// Compact Lap Card (for 400m+ races)
// ═══════════════════════════════════════════════════════════════════

const CompactLapCard = ({ block, onClick, isSelected, isBest, isSlowest }) => {
  const { color: phaseColor } = phaseIcon(block.phase);

  return (
    <button
      onClick={onClick}
      className={`rounded-lg border p-2 text-left transition-all hover:shadow-sm ${isSelected ? 'ring-2 ring-offset-1' : ''}`}
      style={{
        borderColor: isSelected ? T.primary : isBest ? T.success + '60' : T.border,
        ringColor: isSelected ? T.primary : undefined,
        backgroundColor: isSelected ? '#f9fafb' : T.card,
      }}>
      {/* Phase dot + label */}
      <div className="flex items-center gap-1 mb-1">
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: phaseColor }} />
        <span className="text-[10px] font-black" style={{ color: T.text }}>{block.label}</span>
      </div>
      {/* Time */}
      <span className="text-sm font-black block" style={{ color: T.primary }}>{fmtTime(block.timeS)}</span>
      {/* Strokes */}
      <span className="text-[10px] font-bold" style={{ color: T.info }}>{block.strokes} br</span>
    </button>
  );
};


// ═══════════════════════════════════════════════════════════════════
// Main LapBlocks Component
// ═══════════════════════════════════════════════════════════════════

const LapBlocks = ({ session }) => {
  const [selectedLap, setSelectedLap] = useState(null);

  const lapBlocks = session.lapBlocks || [];
  const totalStrokes = session.canonicalStrokeCount || 0;
  const isLongRace = session.isLongRace; // 400m+
  const numLaps = session.numLaps || 1;

  // Find fastest/slowest laps
  const { bestIdx, slowestIdx } = useMemo(() => {
    if (lapBlocks.length <= 1) return { bestIdx: -1, slowestIdx: -1 };
    let best = 0, slowest = 0;
    lapBlocks.forEach((b, i) => {
      if (b.timeS > 0 && (lapBlocks[best].timeS === 0 || b.timeS < lapBlocks[best].timeS)) best = i;
      if (b.timeS > lapBlocks[slowest].timeS) slowest = i;
    });
    return { bestIdx: best, slowestIdx: slowest };
  }, [lapBlocks]);

  if (lapBlocks.length === 0) {
    return (
      <div className="text-center py-4">
        <Layers size={24} className="mx-auto mb-2 opacity-30" style={{ color: T.textLight }} />
        <p className="text-xs" style={{ color: T.textLight }}>
          Dados por parcial não disponíveis — vincule a prova ou processe um vídeo com ≥100m.
        </p>
      </div>
    );
  }

  // ── Short races (50m / 100m / 200m): all expanded ──
  if (!isLongRace) {
    return (
      <div className="space-y-3">
        {/* Summary bar */}
        <div className="flex items-center justify-between text-[10px]" style={{ color: T.textLight }}>
          <span className="font-bold">{numLaps} {numLaps === 1 ? 'trecho' : 'trechos'} de {session.poolLengthM}m</span>
          <span className="font-bold">{totalStrokes} braçadas total</span>
        </div>

        {/* v3.10: Split source indicator */}
        {session.lapBlocksSource && session.lapBlocksSource !== 'none' && (
          <div className="flex items-center gap-1.5 text-[9px] px-2.5 py-1 rounded-lg"
            style={{
              backgroundColor: session.hasRealSplits ? T.successBg : T.warningBg,
              color: session.hasRealSplits ? T.success : T.warning,
            }}>
            <span className="font-bold">
              {session.hasRealSplits ? '✓ Splits reais (viradas detectadas)' : '⚠ Splits estimados (tempo uniforme)'}
            </span>
          </div>
        )}

        <div className={`grid gap-3 ${numLaps <= 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'}`}>
          {lapBlocks.map((block, i) => (
            <ExpandedLapCard
              key={i}
              block={block}
              totalStrokes={totalStrokes}
              isBest={i === bestIdx}
              isSlowest={i === slowestIdx}
            />
          ))}
        </div>

        {/* Pacing type */}
        {session.pacingType && session.pacingType !== 'unknown' && (
          <div className="flex items-center gap-1.5 text-[10px] px-2.5 py-1.5 rounded-lg"
            style={{ backgroundColor: T.infoBg }}>
            <TrendingUp size={11} style={{ color: T.info }} />
            <span className="font-bold" style={{ color: T.text }}>
              Pacing: {session.pacingType === 'negative_split' ? '📈 Negativo (acelerando)' :
                session.pacingType === 'positive_split' ? '📉 Positivo (desacelerando)' :
                '⚖️ Uniforme'}
            </span>
          </div>
        )}
      </div>
    );
  }

  // ── Long races (400m+): compact grid + detail panel ──
  const selectedBlock = selectedLap != null ? lapBlocks[selectedLap] : null;

  return (
    <div className="space-y-3">
      {/* Summary bar */}
      <div className="flex items-center justify-between text-[10px]" style={{ color: T.textLight }}>
        <span className="font-bold">{numLaps} trechos de {session.poolLengthM}m</span>
        <span className="font-bold">{totalStrokes} braçadas total</span>
        {session.pacingType && session.pacingType !== 'unknown' && (
          <span className="font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: T.infoBg, color: T.info }}>
            {session.pacingType === 'negative_split' ? '📈 Negativo' :
              session.pacingType === 'positive_split' ? '📉 Positivo' : '⚖️ Uniforme'}
          </span>
        )}
      </div>

      {/* v3.10: Split source indicator */}
      {session.lapBlocksSource && session.lapBlocksSource !== 'none' && (
        <div className="flex items-center gap-1.5 text-[9px] px-2.5 py-1 rounded-lg"
          style={{
            backgroundColor: session.hasRealSplits ? T.successBg : T.warningBg,
            color: session.hasRealSplits ? T.success : T.warning,
          }}>
          <span className="font-bold">
            {session.hasRealSplits ? '✓ Splits reais (viradas detectadas)' : '⚠ Splits estimados (tempo uniforme)'}
          </span>
        </div>
      )}

      {/* Compact grid */}
      <div className={`grid gap-2 ${
        numLaps <= 8 ? 'grid-cols-4 sm:grid-cols-8' :
        numLaps <= 16 ? 'grid-cols-4 sm:grid-cols-8' :
        'grid-cols-5 sm:grid-cols-10'
      }`}>
        {lapBlocks.map((block, i) => (
          <CompactLapCard
            key={i}
            block={block}
            onClick={() => setSelectedLap(selectedLap === i ? null : i)}
            isSelected={selectedLap === i}
            isBest={i === bestIdx}
            isSlowest={i === slowestIdx}
          />
        ))}
      </div>

      {/* Detail panel for selected lap */}
      {selectedBlock && (
        <div className="border rounded-xl overflow-hidden transition-all" style={{ borderColor: T.primary }}>
          <div className="px-3 py-2 flex items-center justify-between" style={{ backgroundColor: '#f9fafb' }}>
            <span className="text-xs font-black" style={{ color: T.text }}>
              Detalhe: {selectedBlock.label}
            </span>
            <button onClick={() => setSelectedLap(null)} className="p-1 rounded hover:bg-gray-200">
              <ChevronUp size={12} style={{ color: T.textLight }} />
            </button>
          </div>
          <div className="p-3">
            <ExpandedLapCard
              block={selectedBlock}
              totalStrokes={totalStrokes}
              isBest={selectedLap === bestIdx}
              isSlowest={selectedLap === slowestIdx}
            />
          </div>
        </div>
      )}

      {/* Hint */}
      {!selectedBlock && (
        <p className="text-[9px] text-center italic" style={{ color: T.textMuted }}>
          Clique em um bloco para ver detalhes
        </p>
      )}
    </div>
  );
};

export default LapBlocks;
